/**
 * @fileoverview Incident Analysis Page
 * @description Search-driven incident analysis page. Users search for an
 *              incident by ID, the incident is fetched and shown immediately
 *              in a collapsible section, then analysis runs automatically
 *              and results appear in another collapsible section.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Chip,
  Collapse,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Brain,
  Search,
  X,
  ChevronDown,
  FileText,
  CheckCircle2,
} from 'lucide-react';
import Spinner from '@/components/common/Spinner/Spinner';
import { incidentApi } from '../services/incidentApi';
import AnalysisPanel from '../components/AnalysisPanel';
import './IncidentAnalysis.css';

const PHASES = {
  IDLE: 'idle',
  FETCHING: 'fetching',
  ANALYZING: 'analyzing',
  DONE: 'done',
  ERROR: 'error',
};

const PRIORITY_MAP = { 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low', 5: 'Info' };

const IncidentAnalysis = () => {
  // ── State ────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [phase, setPhase] = useState(PHASES.IDLE);
  const [error, setError] = useState(null);
  const [incident, setIncident] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Accordion open state
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [analysisOpen, setAnalysisOpen] = useState(true);

  const abortRef = useRef(0);
  const timerRef = useRef(null);

  // ── Elapsed-time timer ─────────────────────────────────────
  const startTimer = useCallback(() => {
    setElapsedTime(0);
    timerRef.current = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopTimer(), [stopTimer]);

  // ── Search & Auto-Analyze ─────────────────────────────────
  const handleSearch = useCallback(async () => {
    const query = searchQuery.trim();
    if (!query) return;

    const token = ++abortRef.current;

    setError(null);
    setIncident(null);
    setAnalysisResult(null);
    setShowSuccess(false);
    setDetailsOpen(true);
    setAnalysisOpen(true);

    // Phase 1 — fetch the incident
    setPhase(PHASES.FETCHING);
    startTimer();
    try {
      const record = await incidentApi.fetchById(query);
      if (token !== abortRef.current) return;
      if (!record || record.error) {
        stopTimer();
        setError(record?.error || `Incident "${query}" not found.`);
        setPhase(PHASES.ERROR);
        return;
      }
      setIncident(record);

      // Phase 2 — run analysis automatically
      setPhase(PHASES.ANALYZING);
      const incidentId = record.IncidentID ?? query;
      const result = await incidentApi.analyzeIncident(incidentId);
      if (token !== abortRef.current) return;
      stopTimer();
      setAnalysisResult(result);
      setPhase(PHASES.DONE);
      setShowSuccess(true);
    } catch (e) {
      if (token !== abortRef.current) return;
      stopTimer();
      const isBuilding = e.status === 503 && e.details?.code === 'COLLECTION_BUILDING';
      if (isBuilding) {
        setError('The AI analysis engine is initializing after a server restart. Please try again in a minute or two.');
      } else {
        setError(e.message || 'Something went wrong.');
      }
      setPhase(PHASES.ERROR);
    }
  }, [searchQuery, startTimer, stopTimer]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleClear = () => {
    setSearchQuery('');
    setPhase(PHASES.IDLE);
    setError(null);
    setIncident(null);
    setAnalysisResult(null);
    setShowSuccess(false);
    setElapsedTime(0);
    stopTimer();
    abortRef.current++;
  };

  const isLoading = phase === PHASES.FETCHING || phase === PHASES.ANALYZING;

  // ── Render ────────────────────────────────────────────────
  return (
    <Box className="incident-analysis-page" sx={{ p: 3 }}>
      {/* Header */}
      <Box className="page-header" sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} color="primary.main">
          <Brain size={32} style={{ verticalAlign: 'middle', marginRight: 12 }} />
          Incident Intelligence
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Search for an incident by ID to run AI-powered analysis automatically
        </Typography>
      </Box>

      {/* Alerts */}
      <Collapse in={!!error}>
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Collapse>

      {/* ── Search Bar ─────────────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="e.g. 3199921"
          helperText="Paste a ServiceNow Incident ID to run AI-powered root cause analysis"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchQuery && !isLoading && (
                  <IconButton size="small" onClick={handleClear} sx={{ mr: 0.5 }}>
                    <X size={16} />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={handleSearch}
                  disabled={!searchQuery.trim() || isLoading}
                  sx={{
                    color: '#1976d2',
                    '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.08)' },
                    '&.Mui-disabled': { color: 'rgba(25, 118, 210, 0.4)' },
                  }}
                >
                  {isLoading ? (
                    <Spinner size="sm" color="#1976d2" />
                  ) : (
                    <Search size={20} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
        {isLoading && (
          <div className="progress-bar-track" style={{ marginTop: 12 }}>
            <div className="progress-bar-fill" />
          </div>
        )}
      </Paper>

      {/* ── Incident Details (collapsible, shown right after fetch) ── */}
      {incident && phase !== PHASES.FETCHING && (
        <Accordion
          expanded={detailsOpen}
          onChange={() => setDetailsOpen((prev) => !prev)}
          sx={{ mb: 3, borderRadius: '12px !important', '&:before': { display: 'none' } }}
          elevation={1}
        >
          <AccordionSummary
            expandIcon={<ChevronDown size={20} />}
            sx={{ px: 2.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FileText size={18} />
              <Typography variant="subtitle1" fontWeight={700}>
                Incident Details
              </Typography>
              <Chip
                label={`#${incident.IncidentID}`}
                size="small"
                color="primary"
                sx={{ fontWeight: 700 }}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
            {/* Chips row */}
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
              {incident.OwnedByTeam && (
                <Chip label={`Team: ${incident.OwnedByTeam}`} size="small" variant="outlined" />
              )}
              {incident.Category && (
                <Chip label={`Category: ${incident.Category}`} size="small" variant="outlined" />
              )}
              {incident.Service && (
                <Chip label={`Service: ${incident.Service}`} size="small" variant="outlined" />
              )}
              {incident.Status && (
                <Chip label={`Status: ${incident.Status}`} size="small" variant="outlined" />
              )}
              {incident.Priority && (
                <Chip
                  label={`Priority: P${incident.Priority} (${PRIORITY_MAP[incident.Priority] || 'N/A'})`}
                  size="small"
                  color={incident.Priority <= 1 ? 'error' : incident.Priority <= 2 ? 'warning' : 'default'}
                />
              )}
              {incident.CallSource && (
                <Chip label={`Source: ${incident.CallSource}`} size="small" variant="outlined" />
              )}
            </Stack>

            {/* Short Description */}
            {incident.ShortDescription && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Short Description
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                  {incident.ShortDescription}
                </Typography>
              </Box>
            )}

            {/* Description */}
            {incident.Description && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.25 }}>
                  {incident.Description}
                </Typography>
              </Box>
            )}

            {/* Close Description */}
            {incident.CloseDescription && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Close Description
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.25 }}>
                  {incident.CloseDescription}
                </Typography>
              </Box>
            )}

            {/* Cause */}
            {incident.Cause && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Cause
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                  {incident.Cause}
                </Typography>
              </Box>
            )}

            {/* Status Description */}
            {incident.StatusDescription && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Status Description
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25 }}>
                  {incident.StatusDescription}
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* ── Progress Stepper (shown during fetching & analyzing) ── */}
      {(phase === PHASES.FETCHING || phase === PHASES.ANALYZING) && (
        <Paper sx={{ p: 3, mb: 3 }} className="analysis-progress-panel">
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
            <Spinner size={22} color="#01426A" thickness={4} />
            <Typography variant="subtitle1" fontWeight={700} color="primary.main">
              {phase === PHASES.FETCHING
                ? 'Fetching incident details...'
                : 'Running AI analysis — this may take a moment...'}
            </Typography>
            <Chip
              label={`${elapsedTime}s`}
              size="small"
              variant="outlined"
              sx={{ ml: 'auto !important', fontVariantNumeric: 'tabular-nums' }}
            />
          </Stack>

          <Stepper
            activeStep={phase === PHASES.FETCHING ? 0 : 1}
            alternativeLabel
            sx={{ mb: 1 }}
          >
            <Step completed={phase !== PHASES.FETCHING}>
              <StepLabel>Fetch Incident</StepLabel>
            </Step>
            <Step completed={false}>
              <StepLabel>Semantic Search</StepLabel>
            </Step>
            <Step completed={false}>
              <StepLabel>Agentic Validation</StepLabel>
            </Step>
          </Stepper>

          <div className="progress-bar-track">
            <div className="progress-bar-fill" />
          </div>

          {phase === PHASES.ANALYZING && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Comparing with historical incidents using HuggingFace embeddings, then validating with Azure OpenAI...
            </Typography>
          )}
        </Paper>
      )}

      {/* ── Analysis Results (collapsible) ─────────────────── */}
      {analysisResult && phase === PHASES.DONE && (
        <Accordion
          expanded={analysisOpen}
          onChange={() => setAnalysisOpen((prev) => !prev)}
          sx={{ mb: 3, borderRadius: '12px !important', '&:before': { display: 'none' } }}
          elevation={1}
        >
          <AccordionSummary
            expandIcon={<ChevronDown size={20} />}
            sx={{ px: 2.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Brain size={18} />
              <Typography variant="subtitle1" fontWeight={700}>
                Analysis Results
              </Typography>
              <Chip
                label={`#${analysisResult.incident_id}`}
                size="small"
                color="success"
                sx={{ fontWeight: 700 }}
              />
              <Chip
                icon={<CheckCircle2 size={14} />}
                label={`Completed in ${elapsedTime}s`}
                size="small"
                variant="outlined"
                color="success"
                sx={{ fontWeight: 600 }}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
            <AnalysisPanel
              result={analysisResult}
              incident={incident}
              open={false}
              onClose={() => {}}
              inline
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* ── Idle State ─────────────────────────────────────── */}
      {phase === PHASES.IDLE && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Search size={48} style={{ opacity: 0.25, marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary">
            Search for an incident
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Enter an Incident ID above and press Enter or click the search icon.
            The analysis will start automatically.
          </Typography>
        </Paper>
      )}

      {/* ── Success Snackbar ────────────────────────────────── */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={5000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%', fontWeight: 600 }}
          icon={<CheckCircle2 size={20} />}
        >
          Analysis complete for Incident #{analysisResult?.incident_id} — finished in {elapsedTime}s
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IncidentAnalysis;